import React from 'react';
import { ProductCategory } from '../shared/components/ProductCategory';
import { useProducts } from './hooks/use-products';
import { FiltrationBlock } from '../shared/components/FiltrationBlock';
import { useFilter } from '../shared/hooks/use-filter';
import { getProductsByPhaseStep, getPhaseStepTranslationKey } from '../shared/utils/phase';
import {
    TRANSLATIONS_MEALS_LIMIT_REPLACEMENT,
    TRANSLATIONS_PICKED_PRODUCTS_COUNT_REPLACEMENT,
    TRANSLATIONS_DIET_NAME_REPLACEMENT,
} from '../shared/constants';
import { useExpanded } from '../shared/hooks/use-expanded';
import { useCartMealsLocalStorage } from '../shared/hooks/use-cart-meal-local-storage';
import { initComponent } from '../../../../library/react/init-component';
import { useEditMode } from './hooks/use-edit-mode';

const DISABLED_EXPAND_FILTER_OPTION_MIN_WIDTH = 768;
const PHASE_STEPS = [1, 2, 3, 4];

const getPickedProductsAmount = (translations, matchProductCount, packageLimitMeals) => {
    const texts = translations['eshop.configurator.message_part.product_of_meals'].split(
        TRANSLATIONS_PICKED_PRODUCTS_COUNT_REPLACEMENT
    );
    return (
        <span>
            {translations['eshop.configurator.message_part.selected']}: <span>{texts[0]}</span>
            <strong
                className={`${
                    matchProductCount === packageLimitMeals
                        ? 'package-calculation-summary__limit-success'
                        : 'package-calculation-summary__limit-danger'
                }`}
            >
                {matchProductCount}
            </strong>
            {texts[1].replace(TRANSLATIONS_MEALS_LIMIT_REPLACEMENT, packageLimitMeals)}
        </span>
    );
};

const PackageCalculation = ({ defaultData, saveProductApiEndpoint, cartLink, translations }) => {
    const { productCategories, products, cartItemId, phases, flavours, dietName, pickedProductsApi } = defaultData;

    if (!phases || phases.length !== 1) {
        return;
    }

    const { id, breakfastMealCount } = phases[0];

    const packageId = id;
    const packageLimitMeals = breakfastMealCount;

    const phaseStepsProducts = PHASE_STEPS.reduce(
        (prevPhaseStepsProducts, phaseStep) => ({
            ...prevPhaseStepsProducts,
            [phaseStep]: getProductsByPhaseStep(products, phaseStep) || [],
        }),
        {}
    );

    const { activeFilters, toggleFilter, getOptionCount } = useFilter(products);
    const {
        loadProductsFromStorage,
        saveProductsToStorage,
        removeProductsFromStorage,
        savePhaseProductsToStorage,
        savePickedPackageProductsToStorage,
        isLocalStorageClear,
    } = useCartMealsLocalStorage();

    const {
        pickedProducts,
        onDecrementProduct,
        onIncrementProduct,
        onProductAmountChange,
        productAvailabilityMap,
        filteredCategoryProductsByTasteFilters,
        setPickedProducts,
        matchProductsCount,
        onSendPickedProducts,
        hasPickedProductExceedAvailability,
        remainingProductsToCompleteCount,
        hasPhaseStepMealsCompleted,
        pickedProductsCount,
    } = useProducts({
        products,
        cartItemId,
        packageId,
        activeFilters,
        loadProductsFromStorage,
        saveProductsToStorage,
        phaseStepsProducts,
        phaseSteps: PHASE_STEPS,
        translations,
        savePhaseProductsToStorage,
        shouldUpdateOrderAfterProductsChange: !!cartItemId,
        saveProductApiEndpoint,
        removeProductsFromStorage,
        cartLink,
        packageLimitMeals,
    });

    useEditMode(
        savePickedPackageProductsToStorage,
        isLocalStorageClear,
        cartItemId,
        packageId,
        setPickedProducts,
        pickedProductsApi,
        PHASE_STEPS
    );

    const { expanded, toggleExpanded } = useExpanded();

    const getCategoryProducts = (productCategoryId, phaseStep) => {
        const categoryProducts =
            filteredCategoryProductsByTasteFilters && filteredCategoryProductsByTasteFilters[phaseStep]
                ? filteredCategoryProductsByTasteFilters[phaseStep].filter(
                      (product) => product.categoryId === productCategoryId
                  )
                : [];

        return categoryProducts.map((product) => {
            const pickedProduct = pickedProducts[phaseStep].find((pickedProduct) => pickedProduct.id === product.id);

            return { ...product, amount: pickedProduct ? pickedProduct.amount : 0 };
        });
    };

    const isFilterBlockExpanded = window.innerWidth >= DISABLED_EXPAND_FILTER_OPTION_MIN_WIDTH || expanded;

    return (
        <div className="calculation">
            <div className="calculation__description">
                <div>
                    {translations['eshop.configurator.picked_package'].replace(
                        TRANSLATIONS_DIET_NAME_REPLACEMENT,
                        dietName
                    )}
                </div>
                <div>{translations['eshop.configurator.info.process_description']}</div>
            </div>
            {flavours && flavours.length > 0 ? (
                <div>
                    <FiltrationBlock
                        options={flavours}
                        name={translations['eshop.configurator.filtration']}
                        activeFilters={activeFilters}
                        toggleFilter={toggleFilter}
                        matchProductCount={matchProductsCount}
                        getOptionCount={getOptionCount}
                        expanded={isFilterBlockExpanded}
                        toggleExpanded={toggleExpanded}
                        translations={translations}
                    />
                </div>
            ) : null}
            {PHASE_STEPS.map((phaseStep, index) => {
                if (getProductsByPhaseStep(products, phaseStep).length > 0) {
                    return (
                        <div className="product-category-wrapper" key={index}>
                            {productCategories && productCategories.length ? (
                                <>
                                    <div className="package-calculation-phase-step-title">
                                        <h2 className="h2">{translations[getPhaseStepTranslationKey(phaseStep)]}</h2>
                                    </div>
                                    {productCategories.map((productCategory) => {
                                        const categoryProducts = getCategoryProducts(productCategory.id, phaseStep);

                                        const hasCategoryProducts = categoryProducts.length > 0;
                                        if (hasCategoryProducts) {
                                            return (
                                                <ProductCategory
                                                    key={productCategory.id}
                                                    category={productCategory}
                                                    products={categoryProducts}
                                                    onDecrementProduct={(product) =>
                                                        onDecrementProduct(product, phaseStep)
                                                    }
                                                    onIncrementProduct={(product) =>
                                                        onIncrementProduct(product, phaseStep)
                                                    }
                                                    onProductAmountChange={(productId, amount) =>
                                                        onProductAmountChange(productId, amount, phaseStep)
                                                    }
                                                    defaultExpanded={false}
                                                    translations={translations}
                                                    phaseStep={null}
                                                    hasPhaseStepMealsCompleted={hasPhaseStepMealsCompleted}
                                                    remainingProductsToCompleteCount={remainingProductsToCompleteCount}
                                                    productAvailabilityMap={productAvailabilityMap}
                                                />
                                            );
                                        }
                                    })}
                                </>
                            ) : (
                                <div>{translations['eshop.configurator.info.no_category_found']}</div>
                            )}
                        </div>
                    );
                }
            })}

            <div className="package-calculation-summary">
                {getPickedProductsAmount(translations, pickedProductsCount, packageLimitMeals)}
            </div>

            <div className="package-calculation-send-button-wrapper">
                <button
                    className="btn btn--lg btn--green"
                    onClick={() => onSendPickedProducts(undefined)}
                    id="package-calculation-send-offer"
                    disabled={!hasPhaseStepMealsCompleted || hasPickedProductExceedAvailability}
                >
                    <span>{translations['eshop.configurator.action.submit_data']}</span>
                </button>
            </div>
        </div>
    );
};

export const initPackageCalculation = (container, props) => initComponent(container, <PackageCalculation {...props} />);
